/* Platform Admin — SaaS-overzicht over alle workspaces heen.
 * Alleen zichtbaar/toegankelijk voor e-mails in PLATFORM_ADMIN_EMAILS.
 * Toont: totalen, login-grafiek (login_events, gelogd sinds v0.19.0),
 * workspaces-tabel met leden + laatst-actief historiek, recente logins. */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Server, Users, Building2, LogIn, Loader2, ChevronDown, ChevronRight, ShieldAlert,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { api } from "@/lib/api";

const fmtDt = (v) => (v ? new Date(v).toLocaleString() : "—");

function StatTile({ icon: Icon, label, value, testid }) {
    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-4" data-testid={testid}>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">
                <Icon size={12} /> {label}
            </div>
            <div className="text-2xl font-display text-white mt-1.5">{value}</div>
        </div>
    );
}

export default function PlatformAdmin() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [logins, setLogins] = useState(null);
    const [forbidden, setForbidden] = useState(false);
    const [expanded, setExpanded] = useState(new Set());

    useEffect(() => {
        (async () => {
            try {
                const [o, l] = await Promise.all([
                    api.get("/platform-admin/overview"),
                    api.get("/platform-admin/logins?days=30"),
                ]);
                setData(o.data); setLogins(l.data);
            } catch (e) {
                if (e?.response?.status === 403) setForbidden(true);
            }
        })();
    }, []);

    if (forbidden) {
        return (
            <div className="grid place-items-center py-24 text-center" data-testid="padmin-forbidden">
                <ShieldAlert size={32} className="text-[var(--jr-text-3)] mb-3" />
                <p className="text-sm text-[var(--jr-text-2)]">{t("padmin.forbidden")}</p>
            </div>
        );
    }
    if (!data || !logins) {
        return <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-[var(--jr-brand)]" /></div>;
    }

    const toggleWs = (id) => setExpanded((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    return (
        <div data-testid="platform-admin-page">
            <div className="mb-6">
                <h1 className="font-display text-3xl sm:text-4xl tracking-tight flex items-center gap-3">
                    <Server size={26} className="text-[var(--jr-brand)]" /> {t("padmin.title")}
                </h1>
                <p className="text-sm text-[var(--jr-text-2)] mt-1">{t("padmin.subtitle")}</p>
            </div>

            {/* Totalen */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <StatTile icon={Building2} label={t("padmin.stat_workspaces")} value={data.totals.workspaces} testid="padmin-stat-workspaces" />
                <StatTile icon={Users} label={t("padmin.stat_users")} value={data.totals.users} testid="padmin-stat-users" />
                <StatTile icon={LogIn} label={t("padmin.stat_logins_7d")} value={data.totals.logins_7d} testid="padmin-stat-logins7" />
                <StatTile icon={LogIn} label={t("padmin.stat_logins_30d")} value={data.totals.logins_30d} testid="padmin-stat-logins30" />
            </div>

            {/* Login-grafiek 30d */}
            <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-5 mb-6" data-testid="padmin-chart">
                <h2 className="text-base md:text-lg font-medium text-white mb-1">{t("padmin.chart_title")}</h2>
                <p className="text-[11px] text-[var(--jr-text-3)] mb-4">{t("padmin.chart_note")}</p>
                <div style={{ width: "100%", height: 180 }}>
                    <ResponsiveContainer>
                        <BarChart data={logins.series} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                            <XAxis dataKey="date" tick={{ fontSize: 9, fill: "var(--jr-text-3)" }}
                                tickFormatter={(d) => d.slice(5)} interval={Math.max(0, Math.floor(logins.series.length / 10) - 1)} />
                            <YAxis tick={{ fontSize: 9, fill: "var(--jr-text-3)" }} allowDecimals={false} />
                            <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }}
                                contentStyle={{ background: "var(--jr-surface-2)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                            <Bar dataKey="logins" fill="var(--jr-brand)" radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Workspaces-tabel */}
            <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] overflow-x-auto mb-6" data-testid="padmin-workspaces">
                <div className="px-4 py-3 border-b border-white/8">
                    <h2 className="text-base md:text-lg font-medium text-white">{t("padmin.ws_title")}</h2>
                </div>
                <table className="w-full text-sm min-w-[900px]">
                    <thead>
                        <tr className="text-left text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)] border-b border-white/8 bg-white/[0.02]">
                            <th className="px-4 py-2.5">{t("padmin.col_workspace")}</th>
                            <th className="px-4 py-2.5">Tier</th>
                            <th className="px-4 py-2.5">{t("padmin.col_members")}</th>
                            <th className="px-4 py-2.5">{t("padmin.col_logins30")}</th>
                            <th className="px-4 py-2.5">{t("padmin.col_hunts")}</th>
                            <th className="px-4 py-2.5">{t("padmin.col_signals")}</th>
                            <th className="px-4 py-2.5">{t("padmin.col_sent")}</th>
                            <th className="px-4 py-2.5" title={t("padmin.col_credits_tip")}>{t("padmin.col_credits")}</th>
                            <th className="px-4 py-2.5">{t("padmin.col_last_active")}</th>
                            <th className="px-4 py-2.5">{t("padmin.col_created")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.workspaces.map((w) => (
                            <React.Fragment key={w.id}>
                                <tr className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                                    onClick={() => toggleWs(w.id)} data-testid={`padmin-ws-${w.id}`}>
                                    <td className="px-4 py-2.5">
                                        <span className="inline-flex items-center gap-1.5 text-white font-medium">
                                            {expanded.has(w.id) ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                                            {w.name}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5"><span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-white/5">{w.tier || "—"}</span></td>
                                    <td className="px-4 py-2.5 text-xs">{w.member_count}</td>
                                    <td className="px-4 py-2.5 text-xs font-mono">{w.logins_30d}</td>
                                    <td className="px-4 py-2.5 text-xs font-mono" data-testid={`padmin-hunts-${w.id}`}>{w.usage?.hunts ?? 0}</td>
                                    <td className="px-4 py-2.5 text-xs font-mono">{w.usage?.signals ?? 0}</td>
                                    <td className="px-4 py-2.5 text-xs font-mono">{w.usage?.messages_sent ?? 0}</td>
                                    <td className="px-4 py-2.5 text-xs font-mono" title={t("padmin.col_credits_tip")}>
                                        <span className="text-[var(--jr-brand)]">{w.usage?.credits_used ?? 0}</span>
                                        <span className="text-[var(--jr-text-3)]"> / {w.usage?.credits_left ?? 0}</span>
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-[var(--jr-text-2)]">{fmtDt(w.last_active_at)}</td>
                                    <td className="px-4 py-2.5 text-xs text-[var(--jr-text-3)]">{fmtDt(w.created_at)}</td>
                                </tr>
                                {expanded.has(w.id) && (w.members || []).map((m) => (
                                    <tr key={m.user_id} className="border-b border-white/5 bg-white/[0.015]" data-testid={`padmin-member-${m.user_id}`}>
                                        <td className="px-4 py-2 pl-10 text-xs text-[var(--jr-text-2)]">{m.name}</td>
                                        <td className="px-4 py-2 text-[10px] font-mono uppercase text-[var(--jr-text-3)]">{m.role}</td>
                                        <td className="px-4 py-2 text-xs text-[var(--jr-text-3)]" colSpan={6}>{m.email}</td>
                                        <td className="px-4 py-2 text-xs text-[var(--jr-text-2)]">{fmtDt(m.last_active_at)}</td>
                                        <td className="px-4 py-2 text-xs text-[var(--jr-text-3)]">{fmtDt(m.created_at)}</td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Recente logins */}
            <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] overflow-hidden" data-testid="padmin-events">
                <div className="px-4 py-3 border-b border-white/8">
                    <h2 className="text-base md:text-lg font-medium text-white">{t("padmin.events_title")}</h2>
                    <p className="text-[11px] text-[var(--jr-text-3)] mt-0.5">{t("padmin.events_note")}</p>
                </div>
                {logins.events.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-[var(--jr-text-3)]">{t("padmin.events_empty")}</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)] border-b border-white/8 bg-white/[0.02]">
                                <th className="px-4 py-2.5">{t("padmin.col_when")}</th>
                                <th className="px-4 py-2.5">{t("padmin.col_user")}</th>
                                <th className="px-4 py-2.5">Email</th>
                                <th className="px-4 py-2.5">{t("padmin.col_workspace")}</th>
                                <th className="px-4 py-2.5">Event</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logins.events.map((e) => (
                                <tr key={e.id} className="border-b border-white/5" data-testid={`padmin-event-${e.id}`}>
                                    <td className="px-4 py-2 text-xs text-[var(--jr-text-2)]">{fmtDt(e.ts)}</td>
                                    <td className="px-4 py-2 text-xs text-white">{e.name}</td>
                                    <td className="px-4 py-2 text-xs text-[var(--jr-text-3)]">{e.email}</td>
                                    <td className="px-4 py-2 text-xs text-[var(--jr-text-2)]">{e.workspace_name}</td>
                                    <td className="px-4 py-2">
                                        <span className={`text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${e.event === "register" ? "bg-[var(--jr-amber)]/15 text-[var(--jr-amber)] border-[var(--jr-amber)]/40" : "bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border-[var(--jr-brand)]/40"}`}>
                                            {e.event}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
